import type { PostgrestFilterBuilder } from '@supabase/postgrest-js'

/**
 * Every row a query matches, not the first thousand.
 *
 * PostgREST answers an unbounded select with at most `db-max-rows`, which on
 * Supabase is **1000**, and it does so silently — no error, no flag, just a
 * shorter array. Nothing in the app ever passed a range, so:
 *
 *   - `feed` returned 1000 of 1387 rows, and
 *   - `showing_list` returned 1000 of 2716.
 *
 * Doug found it from the far end: "most of the kinos only have two films,
 * whereas I know there are far more per venue." Placement on the Venues
 * screen is built from showings, so a quarter of the showings arriving means
 * a cinema with seven films shows 1.8 of them. He reported two.
 *
 * This is the silent-truncation shape the whole codebase is careful about
 * everywhere else — a run that sees part of a window must not act as though
 * it saw all of it — and it had been sitting in the app's own read path since
 * the first screen was written.
 *
 * **The query must be ordered on something unique and stable.** Keyset
 * pagination over an unordered relation is free to return the same row twice
 * and skip another: Postgres makes no promise about the order of an
 * unordered scan between statements. Ordering by `id` is enough and is what
 * every caller here does.
 */
const PAGE = 1000;

/** A hard stop, so a mistake in a caller cannot page for ever against a
 *  table that is growing while it reads. Well above any table here. */
const MAX_ROWS = 50_000;

// The builder is generic in ways the callers do not use, and pinning them
// here would mean naming the generated Database type at every call site for
// no benefit. The rows come back typed by the caller's own annotation.
/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyQuery = PostgrestFilterBuilder<any, any, any, any, any>

/** The half of PostgrestError the callers read. Narrower than the real type
 *  on purpose: `useQuery` wants a message and nothing here wants more. */
export interface QueryError { message: string }

export async function fetchAll<T>(
  /** Rebuilt per page, because a builder is consumed once it is awaited. */
  query: () => AnyQuery,
): Promise<{ data: T[]; error: QueryError | null }> {
  const out: T[] = []

  for (let from = 0; from < MAX_ROWS; from += PAGE) {
    const { data, error } = await query().range(from, from + PAGE - 1)
    // A page that fails part way through returns what it has *and* the
    // error. Callers check the error first; returning the partial rows is
    // for the one that wants to show something rather than nothing.
    if (error) return { data: out, error: error as QueryError }

    const rows = (data ?? []) as T[]
    out.push(...rows)

    // A short page is the end. Asking again would cost a round trip to be
    // told the same thing.
    if (rows.length < PAGE) return { data: out, error: null }
  }

  return { data: out, error: null }
}

// ---------------------------------------------------------------------------
// The feed on the wire (fix-cold-load)
// ---------------------------------------------------------------------------
//
// Here rather than in `queries.ts` because it is the other half of "every row,
// not the first thousand", and because `queries.ts` imports the client and
// cannot load under `node --test`. Nothing below touches the network.

/** `feed_lite` columns `feed_page` does not send. No reader in `app/src`
 *  names them on a feed row, which was checked by omitting them from
 *  `FeedLiteRow` and typechecking: omit one that is read and the build fails.
 *  The view keeps them for the butler and the chat lookup, which read it
 *  directly. A row cached in IndexedDB before this still carries them, and
 *  nothing reads them there either. Kept although no `FeedRow` reader names
 *  them: `work_id` (`filmGroupKey`) and the `ticket_*` trio (`ticketWords`)
 *  are read through structural parameter types the compiler cannot trace
 *  back to this list. `test/feed-page.test.ts` holds this list and the
 *  migration's column list to exactly `feed_lite` between them. */
export const FEED_WIRE_OMITTED = [
  'venue_hero_source_url', 'ticket_evidence', 'place_kind', 'updated_at',
  'hero_asset_id', 'organisation_id', 'opening_on',
  // `night` and `series` were here from nightlife N1 (20260930100000) until
  // N7 drew them: 20260930170000 puts both on feed_page's list. Every read
  // is `?? null` / `?? []` — a row cached before that has neither key — and
  // CACHE_BUSTER (store.ts) moved so no persisted cache waits for a delta
  // that would never bring them (CLAUDE.md, the E1/E5 trap).
] as const

export type FeedWireOmitted = (typeof FEED_WIRE_OMITTED)[number]

/** What `feed_page` returns: the column names once, and each row as an array
 *  of values in that order. 3.55 MB against `feed_since`'s 7.07 MB for the
 *  same 3,094 rows on 30 Sept; most of the difference was key names. */
export interface FeedPagePayload {
  as_of: string
  cols: string[]
  rows: unknown[][]
  removed: string[]
}

/** The cursor a cache holds when it is not complete from any point in time:
 *  never fetched, or only the first page of a cold pull has arrived. A cache
 *  at this cursor is pulled cold again, whole — it is never asked for a
 *  delta, because a delta from `-infinity` would be every row in one call,
 *  which is the pull this package exists to split. */
export const COLD = '-infinity'

/** How a cold pull is split, in weeks from today. The first page is what
 *  Today paints from (today, the week ahead, and every running exhibition,
 *  which has a start in the past); the second is the rest of the horizon.
 *
 *  Two weeks because that is the furthest Today's own sections look —
 *  `AHEAD_DAYS` (`today.ts`), Openings and Coming up, 14 days inclusive, and
 *  `feed_page`'s `run_start <= current_date + 14` is inclusive too — so the
 *  first page is complete for everything above Today's fold and nothing
 *  more. It was four (1,745 rows, 2.26 MB on 2 Oct) and took 3.5–5.6 s to
 *  land with a 2.5 s throttle, behind `showing_list`'s seven pages on the
 *  same connection; at two it is 1,164 rows, about two thirds of the bytes.
 *  `test/feed-page.test.ts` holds the split to `AHEAD_DAYS`, so widening
 *  Today's window without widening this fails a test rather than quietly
 *  painting a week ahead short. */
export const COLD_SPLIT_WEEKS = 2

/**
 * Whether a cached feed is only the first page of a cold pull: the rows are
 * there, the cursor says `COLD`. The cursor is the only thing that knows —
 * a first page cached at a real `as_of` would be a delta base missing weeks
 * (see `fetchFeed`), so `COLD` on a cache with rows already *means* "partial"
 * and never anything else; this names that rather than adding a flag beside
 * it that could disagree with it. A partial cache whose second page failed
 * is still partial — the query's `error` says it stopped, this says what it
 * holds.
 *
 * What it is for: a screen that counts, ranks across the horizon or says
 * "nothing" must not do so from the first two weeks and call it final, so
 * `useFeed()` treats a partial cache as still loading for every screen but
 * the one that asks for the soonest rows (Today).
 */
export function feedIsPartial(cache: { as_of: string } | undefined): boolean {
  return cache !== undefined && cache.as_of === COLD
}

export interface FeedPageRange {
  from_week: number
  to_week: number
  /** The region the page is of (C2.2): `feed_page`'s fourth argument. Absent
   *  asks the server for no region filter, which is what the call was before
   *  there were regions; the app always sends one. */
  region?: string
}

/** The pages a pull asks for. A warm cache asks once for the whole horizon —
 *  its delta is small, and splitting it would buy nothing but a second
 *  `as_of`. A cold one asks for `[0, split)` then `[split, horizon)`. The
 *  server puts a row in the page holding its `run_start` (a row with none in
 *  the first), so the pages are disjoint and cover exactly `[0, horizon)`. */
export function feedPages(
  cold: boolean, horizonWeeks: number, splitWeeks = COLD_SPLIT_WEEKS, region?: string,
): FeedPageRange[] {
  // `region` goes on every page of a pull or none: a cursor taken under one
  // region's filter is no use to another's, which is why the cache is keyed
  // by it (`useFeed`).
  const pages: FeedPageRange[] = !cold || splitWeeks <= 0 || splitWeeks >= horizonWeeks
    ? [{ from_week: 0, to_week: horizonWeeks }]
    : [{ from_week: 0, to_week: splitWeeks }, { from_week: splitWeeks, to_week: horizonWeeks }]
  return region ? pages.map(p => ({ ...p, region })) : pages
}

/** Whether a row whose `run_start` is `runStartDays` from today (null: no
 *  start) belongs in `range` — `feed_page`'s `where`, in days, restated so
 *  the tests can show the pages partition the window. The app never calls
 *  it; the server decides. Rows already past (`run_end < today`) are
 *  outside every page and not modelled here. */
export function inPage(runStartDays: number | null, { from_week, to_week }: FeedPageRange): boolean {
  if (runStartDays == null) return from_week === 0
  return runStartDays <= to_week * 7 && (from_week === 0 || runStartDays > from_week * 7)
}

/** A page's rows back into objects, keyed by the names the server sent — not
 *  by a list compiled into this bundle, so a column added to or dropped from
 *  `feed_page` later cannot land a value under the wrong name. A row shorter
 *  than `cols` reads its missing tail as `null`: every column is present on
 *  every decoded row, which is the shape `feed_since` always had. */
export function decodeFeedPage<T>(page: Pick<FeedPagePayload, 'cols' | 'rows'>): T[] {
  const { cols, rows } = page
  const out = new Array<T>(rows.length)
  for (let r = 0; r < rows.length; r++) {
    const values = rows[r]
    const row: Record<string, unknown> = {}
    for (let c = 0; c < cols.length; c++) row[cols[c]] = values[c] ?? null
    out[r] = row as T
  }
  return out
}

/** Rows onto a cache: upsert what came, drop what `removed` names, keep the
 *  rest untouched, sorted by id as `feed_since` sent them. A cached row keeps
 *  every field it had until a newer copy replaces it whole — so a row cached
 *  before this change, with the seven columns this no longer sends, still
 *  renders, and one replaced by a decoded row simply lacks them. */
export function mergeFeedRows<T extends { id: string }>(prev: T[], rows: T[], removed: string[]): T[] {
  const byId = new Map(prev.map(r => [r.id, r]))
  for (const row of rows) byId.set(row.id, row)
  for (const id of removed) byId.delete(id)
  return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

// ---------------------------------------------------------------------------
// Showings on the wire (a4-12)
// ---------------------------------------------------------------------------

/** What `showing_page` returns: `feed_page`'s packing — names once, rows as
 *  arrays — plus `venues`, one row per venue the page's showings name, in the
 *  same packing. A showing row carries `venue_id` and not the facts that hang
 *  off it; the venue table carries those once (`venue`, `district`,
 *  `building_id` on 2 Oct), keyed by `venue_id`. */
export interface ShowingPagePayload {
  as_of: string
  cols: string[]
  rows: unknown[][]
  venues: Pick<FeedPagePayload, 'cols' | 'rows'>
}

/** The column the venue table is joined on. Named, not positional, for the
 *  reason `decodeFeedPage` reads `cols`. */
const SHOWING_VENUE_KEY = 'venue_id'

/**
 * A `showing_page` back into `showing_list`-shaped objects: each showing
 * decoded by its own `cols`, then every column of its venue's row but the key
 * laid onto it. A showing with no venue, or with one the table does not name,
 * gets each venue column as `null`, never missing — what `showing_list` sent
 * for a showing with no venue. So a decoded row carries every key the old
 * select named, and nothing downstream of `fetchShowings` can tell the two
 * reads apart.
 *
 * Generic in the row type because the caller's (`ShowingLite`) lives in a
 * component file this one must not import: `node --test` loads this file.
 */
export function decodeShowingPage<T>(page: Pick<ShowingPagePayload, 'cols' | 'rows' | 'venues'>): T[] {
  const showings = decodeFeedPage<Record<string, unknown>>(page)
  const venueCols = page.venues.cols.filter(c => c !== SHOWING_VENUE_KEY)
  const venues = new Map<unknown, Record<string, unknown>>()
  for (const v of decodeFeedPage<Record<string, unknown>>(page.venues)) venues.set(v[SHOWING_VENUE_KEY], v)
  for (const s of showings) {
    const v = s[SHOWING_VENUE_KEY] == null ? undefined : venues.get(s[SHOWING_VENUE_KEY])
    for (const c of venueCols) s[c] = v ? v[c] ?? null : null
  }
  return showings as T[]
}
